import { EnumOwnerType, EnumUserUserType } from "gql/generated";
import {
  EventCreateEditContext,
  EventCreateEditContextProps,
  EventCreateEditContextValues,
} from "./EventCreateEditContext";
import { useEventSteps } from "./useEventSteps";
import { useEventActions } from "./useEventActions";
import { useEventTags } from "./useEventTags";
import { useEventOperatorAndOrganizationList } from "./useEventOperatorAndOrganizationList";
import { useMemo } from "react";
import { MultilingualLangKey } from "shared/types";
import i18n from "i18n";
import { es, fr } from "date-fns/locale";
import { useAppContextOptional } from "useAppContext";

export const EventCreateEditContextProvider = ({
  isOpen,
  initialMode,
  initialStep = 1,
  userEmailAddress,
  userFirstName,
  userLastName,
  userType,
  defaultOrganizationId,
  defaultOperatorEntityIds,
  organizerEntityIds,
  defaultOrganizationIdsForOperatorUser,
  defaultOrganizationTagIds,
  limitHostToOperatorEntityId,
  preloadedOrganizerLocation,
  eventId,
  onClose,
  onEventUpdated,
  onEventDeleted,
  hideMenuButtons,
  isOrganizationAdmin = false,
  isTnbInternalUser: isTnbInternalUserProp = false,
  onOpenPersonDetails,
  personDetailsPersonId = null,
  isPersonDetailsOpen = false,
  onClosePersonDetails = () => {},
  setPersonDetailsPersonId = () => {},
  children,
}: EventCreateEditContextProps) => {
  const appContext = useAppContextOptional();
  const dbUser = appContext?.dbUser;
  const isTnbInternalUser =
    isTnbInternalUserProp || dbUser?.userType === EnumUserUserType.TnbInternal;
  const isOrganizationUser = userType === EnumOwnerType.Organization;
  const isOperatorUser = userType === EnumOwnerType.Operator;

  /**
   * Keep track of the event steps
   */
  const {
    currentStep,
    totalSteps,
    handleNextClick,
    handlePreviousClick,
    handleJumpToStepClick,
    setCurrentStep,
    isVirtual,
    setIsVirtual,
  } = useEventSteps(isOpen, initialStep);

  /**
   * When the eventId changes, we'll need to fetch the event details
   */
  const {
    mode,
    event,
    status,
    loading: isFetchingEvent,
    error: fetchEventError,
    isSaving: isSavingStatusChange,
    isMultiOrgEvent,
    isConfirmationModalOpen,
    confirmationModalMessage,
    handleApproveClick,
    handleAcceptAttentionIssuesClick,
    handleConfirmationModalActionClick,
    handleEditClick,
    handleMenuAction,
    updateEvent,
  } = useEventActions({
    isOpen,
    initialMode,
    onClose,
    onEventUpdated,
    onEventDeleted,
    eventId,
    setCurrentStep,
    isOrganizationUser,
    isOperatorUser,
    defaultOrganizationId,
  });

  /**
   * Fetch the global and organization event tags (used on Step 1)
   * - This will refetch whenever the isOrganizationUser or defaultOrganizationId values change
   */
  const {
    globalEventTags,
    organizationEventTags,
    loading: isLoadingEventTags,
    error: eventTagsError,
  } = useEventTags({
    isOrganization: isOrganizationUser,
    organizationId: defaultOrganizationId,
  });

  /**
   * Fetch all of the operator/organization locations (used on Step 2)
   * - This will refetch whenever the isOpen value changes
   */
  const {
    allLocations,
    searchResults,
    searchOperators,
    prefetchOperatorsByEntityIds,
    isSearchingOperators,
    useOperatorPreload,
    loading: isLoadingOperatorAndOrganizationList,
    preloadError: operatorPreloadError,
  } = useEventOperatorAndOrganizationList({
    isOpen,
    isOrganization: isOrganizationUser,
    isOperator: isOperatorUser,
    defaultOrganizationId,
    defaultOperatorEntityIds,
    defaultOrganizationIdsForOperatorUser,
    organizerEntityIds,
    organizerEntityIdToPrefetch:
      !isOperatorUser && event?.eventOrganizer?.locationInfo?._entityId
        ? event.eventOrganizer.locationInfo._entityId
        : undefined,
    limitHostToOperatorEntityId,
    preloadedOrganizerLocation,
  });

  const contextValue: EventCreateEditContextValues = useMemo(
    () => ({
      isOpen,
      mode,
      userEmailAddress,
      userFirstName,
      userLastName,
      isOwner:
        !!event?.createdByEmail &&
        !!userEmailAddress &&
        event.createdByEmail.toLowerCase().trim() ===
          userEmailAddress.toLowerCase().trim(),
      isOrganizationAdmin,
      isTnbInternalUser,
      userType,
      isOrganizationUser,
      isOperatorUser,
      defaultOrganizationId,
      defaultOperatorEntityIds,
      organizerEntityIds,
      defaultOrganizationIdsForOperatorUser,
      defaultOrganizationTagIds,
      limitHostToOperatorEntityId,
      eventId,
      lang: i18n.language as MultilingualLangKey,
      locale:
        i18n.language === "fr" ? fr : i18n.language === "es" ? es : undefined,
      event,
      isFetchingEvent,
      fetchEventError,
      isSavingStatusChange,
      isMultiOrgEvent,
      isConfirmationModalOpen,
      confirmationModalMessage,
      handleApproveClick,
      handleAcceptAttentionIssuesClick,
      handleConfirmationModalActionClick,
      handleEditClick,
      handleMenuAction,
      updateEvent,
      status,
      loading: isLoadingEventTags || isLoadingOperatorAndOrganizationList,
      error: eventTagsError || operatorPreloadError,
      onClose,
      hideMenuButtons,
      currentStep,
      totalSteps,
      handleNextClick,
      handlePreviousClick,
      handleJumpToStepClick,
      isVirtual,
      setIsVirtual,
      globalEventTags,
      organizationEventTags,
      allLocations,
      searchResults,
      searchOperators,
      prefetchOperatorsByEntityIds,
      isSearchingOperators,
      useOperatorPreload,
      onOpenPersonDetails,
      personDetailsPersonId,
      isPersonDetailsOpen,
      onClosePersonDetails,
      setPersonDetailsPersonId,
    }),
    [
      isOpen,
      mode,
      userEmailAddress,
      userFirstName,
      userLastName,
      isOrganizationAdmin,
      isTnbInternalUserProp,
      dbUser?.userType,
      isTnbInternalUser,
      userType,
      isOrganizationUser,
      isOperatorUser,
      defaultOrganizationId,
      defaultOperatorEntityIds,
      organizerEntityIds,
      defaultOrganizationIdsForOperatorUser,
      defaultOrganizationTagIds,
      limitHostToOperatorEntityId,
      eventId,
      event,
      isFetchingEvent,
      fetchEventError,
      status,
      isSavingStatusChange,
      isMultiOrgEvent,
      isConfirmationModalOpen,
      confirmationModalMessage,
      handleApproveClick,
      handleAcceptAttentionIssuesClick,
      handleConfirmationModalActionClick,
      handleEditClick,
      handleMenuAction,
      updateEvent,
      isLoadingEventTags,
      eventTagsError,
      onClose,
      hideMenuButtons,
      currentStep,
      totalSteps,
      handleNextClick,
      handlePreviousClick,
      handleJumpToStepClick,
      isVirtual,
      setIsVirtual,
      globalEventTags,
      organizationEventTags,
      allLocations,
      searchResults,
      searchOperators,
      prefetchOperatorsByEntityIds,
      isSearchingOperators,
      useOperatorPreload,
      onOpenPersonDetails,
      personDetailsPersonId,
      isPersonDetailsOpen,
      onClosePersonDetails,
      setPersonDetailsPersonId,
      isLoadingOperatorAndOrganizationList,
      operatorPreloadError,
    ],
  );

  return (
    <EventCreateEditContext.Provider value={contextValue}>
      {children}
    </EventCreateEditContext.Provider>
  );
};
